// 261005 E4-P1 — chế độ "Sửa kịch bản" trong khung đọc: thêm/xoá/sửa cảnh và dòng, lưu NGUYÊN tập (PUT một lần, 0 AI).
// Logic ở `scriptEditModel.ts` (thuần, có test). Mốc `expectedModifiedAt` ĐÓNG BĂNG ở `original` (bản lúc bấm "Sửa"), không đọc prop sống.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Card, Input, Modal, Popconfirm, Select, Space, Tag, Typography, message } from 'antd';
import Button from './WbButton';
import { adaptationApi } from '../../../services/api/adaptation';
import type { EpisodeScriptRecord, FilmOutline, ManualSaveResult, ScriptBeatKind } from '../../../types/adaptation';
import {
  ScriptDoc, addBeat, clientValidate, cloneScript, createManualScene, insertScene, isDirty, isManualScene, moveBeat, moveScene, prepareForSave,
  removeBeat, removeScene, removeSceneWarning, updateBeat, updateScene,
} from './scriptEditModel';
import { friendlyApiMessage } from './jobErrorText';
import { INTRO_READONLY_NOTE, INTRO_REMOVE_LABEL, introBeatLabel, isIntroBeat, manualRejectionLines } from './introLineView';

const { Text } = Typography;
const KIND_OPTIONS: { value: ScriptBeatKind; label: string }[] = [
  { value: 'action', label: 'Hành động' }, { value: 'dialogue', label: 'Thoại' }, { value: 'voiceover', label: 'Lời kể (V.O.)' },
];
const NARRATOR_VALUE = '__narrator__';

interface Props {
  storyId: string;
  episode: number;
  /** Bản ĐANG LƯU lúc bấm "Sửa" (đóng băng). `modifiedAt` của nó là mốc CAS. */
  original: EpisodeScriptRecord;
  outline: FilmOutline | null;
  onDirtyChange: (dirty: boolean) => void;
  onCancel: () => void;
  onSaved: (r: ManualSaveResult) => void;
  /** Bản mới hơn đã có ở máy chủ ⇒ thoát chế độ sửa và tải lại (mất phần đang sửa). */
  onReload: () => void;
}

const newId = (): string => Math.random().toString(36).slice(2, 10).padEnd(8, '0');

const ScriptEditor: React.FC<Props> = ({ storyId, episode, original, outline, onDirtyChange, onCancel, onSaved, onReload }) => {
  const base = useMemo(() => original.script as ScriptDoc, [original]);
  const [draft, setDraft] = useState<ScriptDoc>(() => cloneScript(base));
  const [saving, setSaving] = useState(false);
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [conflict, setConflict] = useState(false);
  const dirty = useMemo(() => isDirty(base, draft), [base, draft]);
  const errors = useMemo(() => clientValidate(draft, outline), [draft, outline]);
  const dirtyRef = useRef(dirty);
  dirtyRef.current = dirty;

  useEffect(() => { onDirtyChange(dirty); }, [dirty, onDirtyChange]);
  useEffect(() => () => onDirtyChange(false), [onDirtyChange]);
  useEffect(() => {
    const h = (e: BeforeUnloadEvent) => { if (dirtyRef.current) { e.preventDefault(); e.returnValue = ''; } };
    window.addEventListener('beforeunload', h);
    return () => window.removeEventListener('beforeunload', h);
  }, []);

  const locOptions = (outline?.locations ?? []).map((l) => ({ value: l.id, label: (l.localizedName?.trim() || l.name) }));
  const charOptions = (outline?.characters ?? []).map((c) => ({ value: c.id, label: (c.localizedName?.trim() || c.name) }));
  const edit = (fn: (d: ScriptDoc) => ScriptDoc) => { setServerErrors([]); setDraft((d) => fn(d)); };

  const addSceneAt = (index: number) => edit((d) => {
    const near = d.scenes[Math.max(0, Math.min(index, d.scenes.length) - 1)];
    return insertScene(d, index, createManualScene(near?.locationId || locOptions[0]?.value || '', newId()));
  });
  const delScene = (index: number) => edit((d) => { try { return removeScene(d, index); } catch { return d; } });

  const save = async () => {
    if (errors.length || saving) return;
    setSaving(true); setServerErrors([]);
    try {
      const r = await adaptationApi.saveManualScript(storyId, episode, { script: prepareForSave(draft), expectedModifiedAt: original.modifiedAt ?? '' });
      onSaved(r);
    } catch (e: any) {
      const d = e?.response?.data;
      if (d?.code === 'SCRIPT_CHANGED') setConflict(true);
      else {
        const lines = manualRejectionLines(e?.response?.status, d);   // 422 `MANUAL_LINT_ERROR` / `MANUAL_INTRO_INVALID` (+ violations)
        if (lines) setServerErrors(lines);
        else message.error(friendlyApiMessage(d?.message || e?.message, 'Lưu không được'));
      }
    } finally {
      setSaving(false);
    }
  };

  const AddSceneBar: React.FC<{ at: number }> = ({ at }) => (
    <div style={{ textAlign: 'center' }}><Button size="small" type="dashed" onClick={() => addSceneAt(at)}>+ Thêm cảnh</Button></div>
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12, width: '100%' }}>
      <Alert
        type="info" showIcon message="Đang sửa tay"
        description="Lưu ghi cả tập thành một phiên bản “Sửa tay” (hoàn tác được ở mục Phiên bản). Không gọi AI. Dàn cảnh và bằng chứng nguồn giữ nguyên."
      />
      <AddSceneBar at={0} />
      {draft.scenes.map((sc, i) => {
        const warn = removeSceneWarning(draft, i);
        return (
          <React.Fragment key={sc.scenePlanId ?? i}>
            <Card
              size="small"
              title={<Space size={6}>{`Cảnh ${i + 1}`}{isManualScene(sc) && <Tag color="orange" style={{ margin: 0 }}>Thêm tay</Tag>}</Space>}
              extra={(
                <Space size={4}>
                  <Button size="small" disabled={i === 0} onClick={() => edit((d) => moveScene(d, i, i - 1))}>Lên</Button>
                  <Button size="small" disabled={i === draft.scenes.length - 1} onClick={() => edit((d) => moveScene(d, i, i + 1))}>Xuống</Button>
                  <Popconfirm
                    title="Xoá cảnh này?" okText="Xoá" cancelText="Thôi" disabled={draft.scenes.length <= 1}
                    description={warn.length ? <div style={{ maxWidth: 340 }}>{warn.map((w) => <div key={w}>{w}</div>)}</div> : undefined}
                    onConfirm={() => delScene(i)}
                  >
                    <Button size="small" danger disabled={draft.scenes.length <= 1}>Xoá cảnh</Button>
                  </Popconfirm>
                </Space>
              )}
            >
              <Space direction="vertical" size={8} style={{ width: '100%' }}>
                <Space wrap>
                  <Text strong>Bối cảnh</Text>
                  <Select
                    style={{ minWidth: 220 }} value={sc.locationId || undefined} placeholder="Chọn bối cảnh" options={locOptions}
                    status={sc.locationId ? undefined : 'error'} onChange={(v) => edit((d) => updateScene(d, i, { locationId: v }))}
                  />
                </Space>
                <Input.TextArea
                  value={sc.action} autoSize={{ minRows: 2, maxRows: 8 }} placeholder="Mô tả hành động của cảnh"
                  status={isManualScene(sc) && !sc.action.trim() ? 'error' : undefined}
                  onChange={(e) => edit((d) => updateScene(d, i, { action: e.target.value }))}
                />
                {sc.beats.map((b, j) => (isIntroBeat(b) ? (
                  // 261015 P4c-2c-2 (D10/Q5): dòng V.O. giới thiệu — chữ CHỈ ĐỌC (lấy từ câu đã duyệt ở Tổng quan); chỉ được dời trong cảnh hoặc bỏ cả dòng.
                  <Space key={j} align="start" wrap style={{ width: '100%' }}>
                    <Tag color="blue" style={{ margin: 0 }}>{introBeatLabel(b)}</Tag>
                    <div style={{ minWidth: 260, flex: 1 }}>
                      <div style={{ fontStyle: 'italic' }}>{b.text}</div>
                      <Text type="secondary" style={{ fontSize: 13 }}>{INTRO_READONLY_NOTE}</Text>
                    </div>
                    <Space size={4}>
                      <Button size="small" disabled={j === 0} onClick={() => edit((d) => updateScene2(d, i, (s) => moveBeat(s, j, j - 1)))}>Lên</Button>
                      <Button size="small" disabled={j === sc.beats.length - 1} onClick={() => edit((d) => updateScene2(d, i, (s) => moveBeat(s, j, j + 1)))}>Xuống</Button>
                      <Button size="small" danger onClick={() => edit((d) => updateScene2(d, i, (s) => removeBeat(s, j)))}>{INTRO_REMOVE_LABEL}</Button>
                    </Space>
                  </Space>
                ) : (
                  <Space key={j} align="start" wrap style={{ width: '100%' }}>
                    <Select<ScriptBeatKind> style={{ width: 130 }} value={b.kind} options={KIND_OPTIONS} onChange={(v) => edit((d) => updateScene2(d, i, (s) => updateBeat(s, j, { kind: v })))} />
                    {b.kind !== 'action' && (
                      <Select
                        style={{ width: 170 }} placeholder="Người nói"
                        value={b.characterId ? b.characterId : (b.kind === 'voiceover' ? NARRATOR_VALUE : undefined)}
                        status={b.kind === 'dialogue' && !b.characterId && !b.speakerLabel?.trim() ? 'error' : undefined}
                        allowClear
                        options={b.kind === 'voiceover' ? [{ value: NARRATOR_VALUE, label: 'Người dẫn' }, ...charOptions] : charOptions}
                        onChange={(v) => edit((d) => updateScene2(d, i, (s) => updateBeat(s, j, { characterId: v === NARRATOR_VALUE || v === undefined ? '' : v, ...(v ? { speakerLabel: '' } : {}) })))}
                      />
                    )}
                    {b.kind === 'dialogue' && !b.characterId && (
                      <Input
                        style={{ width: 170 }} placeholder="Vai phụ (nhãn ngắn)" value={b.speakerLabel ?? ''}
                        onChange={(e) => edit((d) => updateScene2(d, i, (s) => updateBeat(s, j, { speakerLabel: e.target.value })))}
                      />
                    )}
                    <Input.TextArea
                      style={{ minWidth: 260, flex: 1 }} value={b.text} autoSize={{ minRows: 1, maxRows: 6 }} placeholder="Chữ"
                      status={b.text.trim() ? undefined : 'error'}
                      onChange={(e) => edit((d) => updateScene2(d, i, (s) => updateBeat(s, j, { text: e.target.value })))}
                    />
                    <Space size={4}>
                      <Button size="small" disabled={j === 0} onClick={() => edit((d) => updateScene2(d, i, (s) => moveBeat(s, j, j - 1)))}>Lên</Button>
                      <Button size="small" disabled={j === sc.beats.length - 1} onClick={() => edit((d) => updateScene2(d, i, (s) => moveBeat(s, j, j + 1)))}>Xuống</Button>
                      <Button size="small" danger onClick={() => edit((d) => updateScene2(d, i, (s) => removeBeat(s, j)))}>Xoá</Button>
                    </Space>
                  </Space>
                )))}
                <Button size="small" onClick={() => edit((d) => updateScene2(d, i, (s) => addBeat(s)))}>+ Thêm dòng</Button>
              </Space>
            </Card>
            <AddSceneBar at={i + 1} />
          </React.Fragment>
        );
      })}

      <div style={{ position: 'sticky', bottom: 0, background: 'var(--wb-bg)', borderTop: '1px solid var(--wb-line-soft)', padding: '10px 0', zIndex: 10 }}>
        {(errors.length > 0 || serverErrors.length > 0) && (
          <Alert
            type="error" showIcon style={{ marginBottom: 8 }} message={serverErrors.length ? 'Máy chủ từ chối lưu' : `Còn ${errors.length} chỗ cần sửa trước khi lưu`}
            description={<ul style={{ margin: 0, paddingLeft: 18, maxHeight: 120, overflow: 'auto' }}>{(serverErrors.length ? serverErrors : errors).map((x, k) => <li key={k}>{x}</li>)}</ul>}
          />
        )}
        <Space>
          <Button type="primary" loading={saving} disabled={errors.length > 0 || !dirty} onClick={save}>Lưu</Button>
          {dirty ? (
            <Popconfirm title="Bỏ các thay đổi chưa lưu?" okText="Bỏ" cancelText="Ở lại" onConfirm={onCancel}><Button disabled={saving}>Huỷ</Button></Popconfirm>
          ) : (
            <Button disabled={saving} onClick={onCancel}>Huỷ</Button>
          )}
          {dirty && <Text type="secondary" style={{ fontSize: 13 }}>Có thay đổi chưa lưu</Text>}
        </Space>
      </div>

      <Modal
        open={conflict} title="Kịch bản vừa được sửa ở nơi khác" closable={false} maskClosable={false}
        okText="Tải lại bản mới (mất phần đang sửa)" cancelText="Ở lại để chép" okButtonProps={{ danger: true }}
        onOk={() => { setConflict(false); onReload(); }} onCancel={() => setConflict(false)}
      >
        Có người/việc khác vừa ghi tập {episode} từ lúc bạn mở. Tải lại sẽ bỏ phần bạn đang sửa; hoặc ở lại để chép nội dung ra trước.
      </Modal>
    </div>
  );
};

/** Sửa MỘT cảnh theo hàm của scene (đổi `scenes[i]`, giữ phần còn lại). */
const updateScene2 = (d: ScriptDoc, i: number, fn: (s: ScriptDoc['scenes'][number]) => ScriptDoc['scenes'][number]): ScriptDoc => (
  i < 0 || i >= d.scenes.length ? d : { ...d, scenes: d.scenes.map((s, k) => (k === i ? fn(s) : s)) });

export default ScriptEditor;
